import React, { useState } from "react";
import { jsPDF } from "jspdf";
import { useAppSelector, useAppDispatch } from "redux/hooks";
import { LoadingButton } from "@mui/lab";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PrintWithDocumentsDialog, {
  DocumentForPdf,
} from "./PrintWithDocumentsDialog";
import headerLogoBase64 from "components/print/templates/headerlogo";
import { compileFinancialsTemplate } from "components/print/compilers/financials";
import { compilePropertyImagesTemplate } from "components/print/compilers/propertyImages";
import { compileSiteStackTemplate } from "components/print/compilers/siteStackPlanImages";
import { compileSummaryTemplate } from "components/print/compilers/summary";
import { compileRecommendationTemplate } from "components/print/compilers/recommendations";
import { compileSwotTemplate } from "components/print/compilers/swot";
import mammoth from "mammoth";
import { PDFDocument } from "pdf-lib";
import { compileSwotGlobalTemplate } from "components/print/compilers/swotGlobal";
import { compileKeyProvisionTemplate } from "components/print/compilers/keyProvisions";
import { compileKeyProvisionInternationalTemplate } from "components/print/compilers/keyProvisionsInternational";
import { compilePostClosingTemplate } from "components/print/compilers/postClosing";
import { compileValuationTemplate } from "components/print/compilers/valuation";
import { compileLeasingTemplate } from "components/print/compilers/leasing";
import { compileCapitalPlanTemplate } from "components/print/compilers/capitalPlan";
import { fetchAssetPlanData } from "components/print/util/printapiCallhelper";
import { compileDebtTemplate } from "./compilers/debt";
import { sustainabilityTemplate } from "./compilers/sustainability";
import { compileComplianceTemplate } from "./compilers/compliance";
import { compilePrioritiesTemplate } from "./compilers/priorities";
import { postApiCall } from "../../services/apiHelper";
import { baseUrl } from "../../config/config";
import moment from "moment";
import {
  rejectionStatus,
  rejectionToast,
} from "../../redux/slices/dialogFormSlice";

/**
 * PrintPdfWithDocuments Component
 *
 * Renders a "PDF + Docs" button that opens a document selection dialog.
 * Selected documents are attached as pages at the end of the generated DAP PDF.
 * Currently supports: Images (JPG, PNG, GIF, BMP), CSV, TXT inline rendering.
 * Other file types show a reference page.
 *
 * NOTE: mammoth (DOCX), exceljs (XLSX), and pdf-lib (PDF merge) have been
 * removed to reduce bundle size. Those features are commented out below
 * and can be re-enabled when bundle size constraints are resolved.
 */
const PrintPdfWithDocuments: React.FC = () => {
  const dispatch = useAppDispatch();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);

  const investmentId =
    useAppSelector((state) => state.summary?.dapData?.investmentId) || "";
  const propertyType =
    useAppSelector((state) => state.summary?.dapData?.propertyType) || "";
  const budgetYear =
    useAppSelector((state) => state.summary?.dapData?.budgetYear) || "";
  const assetPlanId =
    useAppSelector((state) => state.summary?.dapData?.assetPlanId) || "";
  const investmentName =
    useAppSelector((state) => state.summary?.dapData?.dealName) || "";
  const internationalInd =
    useAppSelector((state) => state.summary?.dapData?.internationalInd) || "";

  const margins = { top: 70, right: 40, bottom: 40, left: 40 };

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (!pdfGenerating) {
      setDialogOpen(false);
    }
  };

  const handleConfirmAndGenerate = (selectedDocuments: DocumentForPdf[]) => {
    setPdfGenerating(true);
    generateDapPdfWithDocuments(selectedDocuments);
  };

  const generateDapPdfWithDocuments = async (
    selectedDocuments: DocumentForPdf[]
  ) => {
    const pdfInstance = new jsPDF("p", "pt");
    const width = pdfInstance.internal.pageSize.getWidth();

    const assetPlanDetails = {
      assetPlanId: assetPlanId,
      investmentId: investmentId,
      budgetYear: budgetYear,
      investmentName: investmentName,
      propertyType: propertyType,
      internationalInd: internationalInd,
    };

    try {
      const [assetPlanData, attachmentData] = await Promise.all([
        fetchAssetPlanData(assetPlanDetails),
        fetchDocumentAttachments(selectedDocuments),
      ]);

      if (assetPlanData.error) {
        handleError("Export to PDF failed. Please try again!");
        return;
      }

      const htmlContents = getHtmlContents(assetPlanData);
      const pageHeight = pdfInstance.internal.pageSize.getHeight();

      addSectionToPdf(
        0,
        htmlContents,
        pdfInstance,
        pageHeight,
        width,
        assetPlanDetails,
        attachmentData
      );
    } catch (error) {
      console.error("Error generating PDF with documents:", error);
      handleError("Export to PDF failed. Please try again!");
    }
  };

  const fetchDocumentAttachments = async (
    selectedDocuments: DocumentForPdf[]
  ): Promise<any> => {
    if (!selectedDocuments.length) return { attachments: [] };

    try {
      const attachmentPromises = selectedDocuments.map(async (doc) => {
        try {
          // If pre-signed URL is available, fetch directly from S3
          if (doc.preSignedUrl) {
            try {
              const response = await fetch(doc.preSignedUrl);
              if (response.ok) {
                const blob = await response.blob();
                const base64Content = await blobToBase64(blob);
                return {
                  fileKey: doc.fileKey,
                  displayName: doc.displayName,
                  fileType: doc.fileType,
                  base64Content: base64Content,
                };
              }
            } catch (presignedErr) {
              console.warn(`[PDF+Docs] Pre-signed URL failed for ${doc.displayName}`);
            }
          }

          // Fallback: use the existing GetDocumentToDownload API endpoint
          const apiResponse: any = await postApiCall(
            `${baseUrl}Document/GetDocumentToDownload`,
            { fileKey: doc.fileKey }
          );

          if (apiResponse) {
            return {
              fileKey: doc.fileKey,
              displayName: doc.displayName,
              fileType: doc.fileType,
              base64Content: typeof apiResponse === "string" ? apiResponse : apiResponse?.data || apiResponse,
            };
          }
          return null;
        } catch (err) {
          console.error(`[PDF+Docs] Error fetching document ${doc.displayName}:`, err);
          return null;
        }
      });

      const results = await Promise.all(attachmentPromises);
      const attachments = results.filter((r) => r !== null);
      return { attachments };
    } catch (error) {
      console.error("[PDF+Docs] Error in fetchDocumentAttachments:", error);
      return { attachments: [] };
    }
  };

  const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        const base64 = result.split(",")[1] || result;
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const handleError = (message: string) => {
    setPdfGenerating(false);
    setDialogOpen(false);
    dispatch(rejectionToast(true));
    dispatch(rejectionStatus({ message: message, severity: "error" }));
    setTimeout(() => { dispatch(rejectionToast(false)); }, 4000);
  };

  const addSectionToPdf = (
    index: number,
    htmlContents: string[],
    pdfInstance: jsPDF,
    pageHeight: number,
    width: number,
    assetPlanDetails: any,
    attachmentData: any
  ) => {
    const htmlContent = htmlContents[index];
    pdfInstance.html(htmlContent, {
      callback: (doc) => {
        index++;
        if (index < htmlContents.length) {
          addSectionToPdf(index, htmlContents, doc, pageHeight, width, assetPlanDetails, attachmentData);
        } else {
          generateFinalPdfWithAttachments(doc, assetPlanDetails, attachmentData);
        }
      },
      x: 0,
      y: index === 0 ? 0 : getPositionY((pdfInstance as any).internal.pages.length, pageHeight),
      autoPaging: htmlContent.includes("Physical Risk Consideration") ? "slice" : "text",
      width: width - (margins.left + margins.right),
      windowWidth: width,
      margin: Object.values(margins),
    });
  };

  /**
   * Renders document attachments as pages in the PDF.
   * Supports: Images (inline), CSV/TXT (text rendering).
   * Other types get a reference page.
   */
  const generateFinalPdfWithAttachments = async (
    finalDoc: jsPDF,
    assetPlanDetails: any,
    attachmentData: any
  ) => {
    // Track PDF attachments for later merge with pdf-lib
    const pdfInsertions: { attachment: any; insertAfterPage: number }[] = [];

    if (attachmentData?.attachments?.length > 0) {
      const pageWidth = finalDoc.internal.pageSize.getWidth();
      const pageHeight = finalDoc.internal.pageSize.getHeight();

      // Separator page
      finalDoc.addPage();
      finalDoc.setFontSize(20);
      finalDoc.setTextColor("#253746");
      finalDoc.text("Document Attachments", pageWidth / 2, pageHeight / 3, { align: "center" });
      finalDoc.setFontSize(12);
      finalDoc.setTextColor("black");
      finalDoc.text(
        `${attachmentData.attachments.length} document(s) attached below`,
        pageWidth / 2, pageHeight / 3 + 30, { align: "center" }
      );

      for (let idx = 0; idx < attachmentData.attachments.length; idx++) {
        const attachment = attachmentData.attachments[idx];
        const fileType = (attachment.fileType || "").toLowerCase();
        const imageTypes = [".jpg", ".jpeg", ".png", ".gif", ".bmp"];

        if (imageTypes.includes(fileType) && attachment.base64Content) {
          // Images: embed directly
          finalDoc.addPage();
          const yStart = margins.top + 20;
          finalDoc.setFontSize(12);
          finalDoc.setTextColor("#253746");
          finalDoc.text(`Attachment ${idx + 1}: ${attachment.displayName}`, margins.left, yStart);

          try {
            const imgFormat = (fileType === ".jpg" || fileType === ".jpeg") ? "JPEG"
              : fileType === ".png" ? "PNG" : fileType === ".gif" ? "GIF" : "JPEG";
            const maxWidth = pageWidth - margins.left - margins.right;
            const maxHeight = pageHeight - yStart - 40 - margins.bottom;

            // Load image to get natural dimensions for aspect-ratio-correct rendering
            const img = new Image();
            img.src = `data:image/${imgFormat.toLowerCase()};base64,${attachment.base64Content}`;
            await new Promise<void>((resolve) => { img.onload = () => resolve(); img.onerror = () => resolve(); });

            let renderWidth = maxWidth;
            let renderHeight = maxHeight;
            if (img.naturalWidth && img.naturalHeight) {
              const aspectRatio = img.naturalWidth / img.naturalHeight;
              if (renderWidth / renderHeight > aspectRatio) {
                renderWidth = renderHeight * aspectRatio;
              } else {
                renderHeight = renderWidth / aspectRatio;
              }
            }

            finalDoc.addImage(
              `data:image/${imgFormat.toLowerCase()};base64,${attachment.base64Content}`,
              imgFormat, margins.left, yStart + 25, renderWidth, renderHeight, undefined, "NONE"
            );
          } catch (imgError) {
            console.error("Error embedding image:", imgError);
            finalDoc.setTextColor("black");
            finalDoc.text("Image could not be rendered.", margins.left, yStart + 40);
          }
        } else if ((fileType === ".csv" || fileType === ".txt") && attachment.base64Content) {
          // CSV / TXT: render as text or table
          try {
            const textContent = atob(attachment.base64Content);
            finalDoc.addPage();
            let yStart = margins.top + 20;
            const usableWidth = pageWidth - margins.left - margins.right;

            finalDoc.setFontSize(13);
            finalDoc.setTextColor("#253746");
            finalDoc.text(`Attachment ${idx + 1}: ${attachment.displayName}`, margins.left, yStart);
            yStart += 20;

            if (fileType === ".csv") {
              const rows = textContent.split("\n").map((row) => row.split(",").map((cell) => cell.trim().replace(/^"|"$/g, "")));
              yStart = renderTableInPdf(finalDoc, rows, margins, pageWidth, pageHeight, yStart);
            } else {
              finalDoc.setFontSize(9);
              finalDoc.setTextColor("black");
              const paragraphs = textContent.split("\n");
              const lineHeight = 12;
              const maxY = pageHeight - margins.bottom;
              for (const para of paragraphs) {
                if (para.trim() === "") { yStart += lineHeight * 0.5; }
                else {
                  const wrapped = finalDoc.splitTextToSize(para, usableWidth);
                  for (const line of wrapped) {
                    if (yStart + lineHeight > maxY) { finalDoc.addPage(); yStart = margins.top + 10; }
                    finalDoc.text(line, margins.left, yStart);
                    yStart += lineHeight;
                  }
                }
              }
            }
          } catch (txtError) {
            console.error("Error rendering text/CSV file:", txtError);
          }
        } else if (
          (fileType === ".docx" || fileType === ".doc") && attachment.base64Content
        ) {
          // DOCX: extract text using mammoth and render as pages
          try {
            const binaryString = atob(attachment.base64Content);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            const result = await (mammoth as any).extractRawText({ arrayBuffer: bytes.buffer });
            const textContent = result.value || "";

            finalDoc.addPage();
            const usableWidth = pageWidth - margins.left - margins.right;
            let yStart = margins.top + 20;

            finalDoc.setFontSize(13);
            finalDoc.setTextColor("#253746");
            finalDoc.text(`Attachment ${idx + 1}: ${attachment.displayName}`, margins.left, yStart);
            yStart += 20;

            finalDoc.setFontSize(9);
            finalDoc.setTextColor("black");
            const paragraphs = textContent.split("\n");
            const lineHeight = 12;
            const maxY = pageHeight - margins.bottom;

            for (const para of paragraphs) {
              if (para.trim() === "") { yStart += lineHeight * 0.5; }
              else {
                const wrapped = finalDoc.splitTextToSize(para, usableWidth);
                for (const line of wrapped) {
                  if (yStart + lineHeight > maxY) { finalDoc.addPage(); yStart = margins.top + 10; }
                  finalDoc.text(line, margins.left, yStart);
                  yStart += lineHeight;
                }
              }
            }
          } catch (docxError) {
            console.error("Error converting DOCX:", docxError);
            finalDoc.addPage();
            finalDoc.setFontSize(12);
            finalDoc.setTextColor("black");
            finalDoc.text(`Attachment ${idx + 1}: ${attachment.displayName}`, margins.left, margins.top + 20);
            finalDoc.text("Document could not be rendered. Please refer to the original file.", margins.left, margins.top + 40);
          }
        } else if (fileType === ".pdf" && attachment.base64Content) {
          // PDF: add separator page, actual pages merged after via pdf-lib
          finalDoc.addPage();
          const separatorPageNum = (finalDoc as any).internal.getNumberOfPages();
          pdfInsertions.push({ attachment, insertAfterPage: separatorPageNum });
          const yStart = margins.top + 20;
          finalDoc.setFontSize(13);
          finalDoc.setTextColor("#253746");
          finalDoc.text(`Attachment ${idx + 1}: ${attachment.displayName}`, margins.left, yStart);
          finalDoc.setFontSize(10);
          finalDoc.setTextColor("black");
          finalDoc.text("The following pages contain the content of this PDF document.", margins.left, yStart + 20);
        } else {
          // Other file types: reference page
          finalDoc.addPage();
          const yStart = margins.top + 20;
          finalDoc.setFontSize(13);
          finalDoc.setTextColor("#253746");
          finalDoc.text(`Attachment ${idx + 1}: ${attachment.displayName}`, margins.left, yStart);
          finalDoc.setFontSize(10);
          finalDoc.setTextColor("black");
          finalDoc.text(`File Type: ${fileType.replace(".", "").toUpperCase() || "Unknown"}`, margins.left, yStart + 20);
          finalDoc.text(
            "This file type cannot be rendered inline. Please refer to the original document in the DAP system.",
            margins.left, yStart + 40
          );
        }
      }
    }

    // Add headers and footers to all pages
    addHeaderFooters(finalDoc, assetPlanDetails);

    // Merge PDF attachments at correct positions using pdf-lib
    if (pdfInsertions.length > 0) {
      try {
        const jsPdfBytes = finalDoc.output("arraybuffer");
        const mergedPdf = await PDFDocument.load(jsPdfBytes);

        let cumulativeOffset = 0;
        for (let i = 0; i < pdfInsertions.length; i++) {
          const { attachment: pdfAttachment, insertAfterPage } = pdfInsertions[i];
          try {
            const attachmentBytes = Uint8Array.from(atob(pdfAttachment.base64Content), (c) => c.charCodeAt(0));
            const attachedPdf = await PDFDocument.load(attachmentBytes, { ignoreEncryption: true });
            const copiedPages = await mergedPdf.copyPages(attachedPdf, attachedPdf.getPageIndices());
            const insertAtIndex = insertAfterPage + cumulativeOffset;
            for (let p = 0; p < copiedPages.length; p++) {
              mergedPdf.insertPage(insertAtIndex + p, copiedPages[p]);
            }
            cumulativeOffset += copiedPages.length;
          } catch (mergeErr) {
            console.error(`[PDF+Docs] Error merging PDF ${pdfAttachment.displayName}:`, mergeErr);
          }
        }

        const mergedBytes = await mergedPdf.save();
        const blob = new Blob([mergedBytes as unknown as BlobPart], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = getPdfFileName(assetPlanDetails);
        link.click();
        URL.revokeObjectURL(url);
      } catch (pdfMergeError) {
        console.error("[PDF+Docs] Error in PDF merge:", pdfMergeError);
        finalDoc.save(getPdfFileName(assetPlanDetails));
      }
    } else {
      finalDoc.save(getPdfFileName(assetPlanDetails));
    }
    setPdfGenerating(false);
    setDialogOpen(false);
  };

  const renderTableInPdf = (
    doc: jsPDF, rows: string[][],
    docMargins: { top: number; right: number; bottom: number; left: number },
    pgWidth: number, pgHeight: number, startY: number
  ): number => {
    if (rows.length === 0) return startY;
    const usableWidth = pgWidth - docMargins.left - docMargins.right;
    const maxCols = Math.min(rows[0].length, 10);
    const colWidth = usableWidth / maxCols;
    const rowHeight = 14;
    const headerRowHeight = 16;
    const maxY = pgHeight - docMargins.bottom;
    let yPos = startY;
    const maxRows = Math.min(rows.length, 200);

    for (let rowIdx = 0; rowIdx < maxRows; rowIdx++) {
      const row = rows[rowIdx];
      const isHeader = rowIdx === 0;
      const currentRowHeight = isHeader ? headerRowHeight : rowHeight;

      if (yPos + currentRowHeight > maxY) {
        doc.addPage();
        yPos = docMargins.top + 10;
        if (rowIdx > 0 && rows[0]) {
          doc.setFillColor(37, 55, 70);
          doc.rect(docMargins.left, yPos, usableWidth, headerRowHeight, "F");
          doc.setFontSize(7.5);
          doc.setTextColor("#fff");
          for (let colIdx = 0; colIdx < maxCols; colIdx++) {
            doc.text((rows[0][colIdx] || "").substring(0, 20), docMargins.left + colIdx * colWidth + 3, yPos + 11);
          }
          yPos += headerRowHeight;
        }
      }

      if (isHeader) {
        doc.setFillColor(37, 55, 70);
        doc.rect(docMargins.left, yPos, usableWidth, headerRowHeight, "F");
        doc.setFontSize(7.5);
        doc.setTextColor("#fff");
        for (let colIdx = 0; colIdx < maxCols; colIdx++) {
          doc.text((row[colIdx] || "").substring(0, 20), docMargins.left + colIdx * colWidth + 3, yPos + 11);
        }
        yPos += headerRowHeight;
      } else {
        if (rowIdx % 2 === 0) {
          doc.setFillColor(240, 240, 240);
          doc.rect(docMargins.left, yPos, usableWidth, rowHeight, "F");
        }
        doc.setFontSize(7);
        doc.setTextColor("black");
        for (let colIdx = 0; colIdx < maxCols; colIdx++) {
          doc.text((row[colIdx] || "").substring(0, 22), docMargins.left + colIdx * colWidth + 3, yPos + 10);
        }
        yPos += rowHeight;
      }
    }

    if (rows.length > 200) {
      yPos += 10;
      doc.setFontSize(8);
      doc.setTextColor("black");
      doc.text(`(${rows.length - 200} additional rows not shown)`, docMargins.left, yPos);
      yPos += 12;
    }
    return yPos;
  };

  // ---- Shared helper methods ----

  const escapeInlineStyle = (str: string): string => str.replaceAll("style=", "");

  const getPositionY = (numberOfPages: number, pageHeight: number): number => {
    return (numberOfPages - 1) * (pageHeight - (margins.top + margins.bottom));
  };

  const getPdfFileName = (assetPlanDetails: any): string => {
    return `DAP ${assetPlanDetails.budgetYear} - ${assetPlanDetails.investmentId} - ${moment().format("MMM-DD-YYYY hhmm A")}.pdf`;
  };

  const addHeaderFooters = (doc: jsPDF, assetPlanDetails: any) => {
    const pageCount = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      addHeader(doc, assetPlanDetails);
      doc.setFontSize(8);
      doc.setTextColor("black");
      doc.text("Page " + String(i) + " of " + String(pageCount), doc.internal.pageSize.width / 2, 820, { align: "center" });
    }
  };

  const addHeader = (doc: jsPDF, assetPlanDetails: any) => {
    if (headerLogoBase64) {
      doc.addImage(headerLogoBase64, "PNG", margins.left, 10, 80, 30);
    }
    doc.setFontSize(16);
    doc.setTextColor("green");
    doc.text(`${assetPlanDetails.budgetYear} Digital Asset Plan`, margins.left + 90, 25, { align: "left" });
    doc.setFontSize(12);
    doc.text(`${assetPlanDetails.investmentId} - ${assetPlanDetails.investmentName}`, margins.left + 90, 40, { align: "left" });
  };

  const getHtmlContents = (assetPlanData: any): string[] => {
    const sections: string[] = [];

    if (assetPlanData.summaryData) {
      const html = compileSummaryTemplate(assetPlanData.summaryData, assetPlanData.comments);
      if (html) sections.push(escapeInlineStyle(html));
    }
    if (assetPlanData.propertyImages?.length) {
      const html = compilePropertyImagesTemplate(assetPlanData.propertyImages);
      if (html?.length) sections.push(...html);
    }
    if (assetPlanData.recomData?.length || assetPlanData.comments?.some((c: any) => c.sectionName?.toLowerCase() === "missionstatement")) {
      const html = compileRecommendationTemplate(assetPlanData.recomData, assetPlanData.comments, parseInt(budgetYear), internationalInd);
      if (html) sections.push(escapeInlineStyle(html));
    }
    if (internationalInd === "Y" && assetPlanData.prioritiesData && Object.keys(assetPlanData.prioritiesData).length > 0) {
      const html = compilePrioritiesTemplate(assetPlanData.prioritiesData);
      if (html) sections.push(escapeInlineStyle(html));
    }
    if (assetPlanData.complianceData && Object.keys(assetPlanData.complianceData).length > 0) {
      const html = compileComplianceTemplate(assetPlanData.complianceData);
      if (html) sections.push(escapeInlineStyle(html));
    }
    if (assetPlanData.sustainabilityData && Object.keys(assetPlanData.sustainabilityData).length > 0) {
      const data = sustainabilityTemplate(assetPlanData.sustainabilityData);
      if (data?.length) sections.push(...data.map((t: string) => escapeInlineStyle(t)));
    }
    if (assetPlanData.sitePlans?.length) {
      const html = compileSiteStackTemplate(assetPlanData.sitePlans, "SitePlan");
      if (html?.length) sections.push(...html);
    }
    if (assetPlanData.stackingPlans?.length) {
      const html = compileSiteStackTemplate(assetPlanData.stackingPlans, "StackingPlan");
      if (html?.length) sections.push(...html);
    }
    if ((assetPlanData.financials && Object.keys(assetPlanData.financials).length > 0) || assetPlanData.comments?.some((c: any) => c.sectionName?.toLowerCase() === "financials")) {
      const html = compileFinancialsTemplate(assetPlanData.financials, budgetYear, assetPlanData.comments);
      if (html) sections.push(html);
    }
    if ((assetPlanData.capitalPlanTable1?.length || assetPlanData.capitalPlanTable2?.length) || assetPlanData.comments?.some((c: any) => c.sectionName?.toLowerCase() === "capitalplan")) {
      const html = compileCapitalPlanTemplate(assetPlanData.capitalPlanTable1, assetPlanData.capitalPlanTable2, budgetYear, assetPlanData.comments);
      if (html) sections.push(html);
    }
    if (assetPlanData.comments?.length && assetPlanData.comments.some((c: any) => c.sectionName?.toLowerCase() === "swot")) {
      const html = internationalInd === "Y" ? compileSwotGlobalTemplate(assetPlanData.comments) : compileSwotTemplate(assetPlanData.comments);
      if (html) sections.push(escapeInlineStyle(html));
    }
    if ((assetPlanData.residential.data || assetPlanData.leaseExpirations.data || assetPlanData.commertial.data || assetPlanData.keyTenants.data) || assetPlanData.comments?.some((c: any) => c.sectionName?.toLowerCase() === "leasing")) {
      const html = compileLeasingTemplate(assetPlanData.residential, assetPlanData.leaseExpirations, budgetYear, assetPlanData.commertial, assetPlanData.keyTenants, assetPlanData.comments, propertyType);
      if (html?.length) sections.push(...html.map((h: string) => escapeInlineStyle(h)));
    }
    if (assetPlanData.valuationData?.length || assetPlanData.comments?.some((c: any) => c.sectionName?.toLowerCase() === "valuation")) {
      const html = compileValuationTemplate(assetPlanData.valuationData, propertyType, assetPlanData.comments, internationalInd);
      if (html) sections.push(escapeInlineStyle(html));
    }
    if (assetPlanData.debtData && Object.keys(assetPlanData.debtData).length > 1 && assetPlanData.debtData.assetPlanId !== "00000000-0000-0000-0000-000000000000") {
      const html = compileDebtTemplate(assetPlanData.debtData);
      if (html) sections.push(escapeInlineStyle(html));
    }
    if ((assetPlanData.keyProvisionData && assetPlanData.keyProvisionData.assetPlanId !== "00000000-0000-0000-0000-000000000000") || assetPlanData.comments?.some((c: any) => c.sectionName?.toLowerCase() === "keyprovisions")) {
      const html = internationalInd === "Y"
        ? compileKeyProvisionInternationalTemplate(assetPlanData.comments, assetPlanData.keyProvisionData)
        : compileKeyProvisionTemplate(assetPlanData.comments, assetPlanData.keyProvisionData, assetPlanData.propertyType, assetPlanData.JVData);
      if (html) sections.push(escapeInlineStyle(html));
    }
    if (assetPlanData.comments?.length && assetPlanData.comments.some((c: any) => c.sectionName?.toLowerCase() === "postclosing")) {
      const html = compilePostClosingTemplate(assetPlanData.comments);
      if (html) sections.push(escapeInlineStyle(html));
    }
    return sections;
  };

  return (
    <div style={{ position: "relative", display: "inline-block", whiteSpace: "nowrap" }}>
      <LoadingButton
        loading={pdfGenerating && !dialogOpen}
        variant="contained"
        onClick={handleOpenDialog}
        className="primary"
        size="small"
        sx={{ lineHeight: 2.2, ml: 1 }}
      >
        <PictureAsPdfIcon sx={{ mr: "2.5px" }} />
        Export to PDF
      </LoadingButton>

      <PrintWithDocumentsDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onConfirm={handleConfirmAndGenerate}
        assetPlanId={assetPlanId}
        loading={pdfGenerating}
      />
    </div>
  );
};

export default PrintPdfWithDocuments;
